<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('kpiDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row mb-3">
            <div class="col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="col-md-4">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
            <div class="col-md-4">
                <label class="form-label" for="evaluationMethod" required>{{ $t('evaluationMethod') }}</label>
                <select id="evaluationMethod" class="form-select" disabled v-model="data.evaluationMethod">
                    <option disabled>{{ $t('select') }}</option>
                    <option value="cwpc">{{ $t('cwpc') }}</option>
                </select>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-2">
                <input class="form-check-input" v-model="data.toTheResults" type="checkbox" id="toTheResults">
                <label class="form-check-label ps-2"  for="toTheResults">
                    {{ $t('toTheResults') }}
                </label>
            </div>
        </div>
        <div class="mb-3">
            <h4>{{ $t('kpiClassification') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('startClassification') }}</th>
                        <th scope="col">{{ $t('endClassification') }}</th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('color') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(kpiClassification, i) in data.kpiClassifications" class="align-middle"  :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removeKpiClassification(i)"></i></th>
                        <td>
                            <input v-model="kpiClassification.startClassification" :disabled="i > 0" type="number" class="form-control" :id="'startClassification_' + i">
                        </td>
                        <td>
                            <input v-model="kpiClassification.endClassification" @input="updateKpiClassificationStart(kpiClassification.endClassification, i)"  type="number" class="form-control" :id="'endClassification_' + i">
                        </td>
                        <td>
                            <input v-model="kpiClassification.name" type="string" class="form-control" :id="'name_' + i">
                        </td>
                        <td>
                            <input v-model="kpiClassification.color" type="color" class="form-control" :id="'color_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addKpiClassification"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteKpi">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
        <KPIDeleteModal v-if="showKeyPerformanceIndicatorDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showKeyPerformanceIndicatorDeleteModal = false"/>
    </div>
</template>

<script>
import KeyPerformanceIndicatorApi from "../../api/server/kpi";
import DatasetApi from "../../api/server/dataset";
import AssetTypeApi from "../../api/server/assetType";
import KPIDeleteModal from './KPIDeleteModal.vue';
import ModelingApi from "../../api/server/modeling";
import { CheckIfUserIsEditor } from '../../authCache';

export default {
    name: "KeyPerformanceIndicatorDetail",
    watch: {
        $route: "requestData",
    },
    components: {
        KPIDeleteModal,
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            this.data = await KeyPerformanceIndicatorApi.kpiDetail(this.$route.params.id, this.$route.params.keyPerformanceIndicatorId);
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);
            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
            
                await KeyPerformanceIndicatorApi.kpiUpdate(this.$route.params.id, this.data);
                this.showSuccess();
            } else {
                this.showEmptyNameError();
            }
        },
        showDeleteKpi() {
            this.keyPerformanceIndicatorToDeleteId = this.data.id;
            this.showKeyPerformanceIndicatorDeleteModal = true;
        },
        removeKpiClassification(rowIndex) {
            this.data.kpiClassifications.splice(rowIndex, 1);
        },
        addKpiClassification() {
            this.data.kpiClassifications.push({  
                color: "#000000",
                name: "",
                startClassification: this.getMinStartKpiClassification(),
                endClassification: 0.0
            })
        },
        updateKpiClassificationStart(value, i) {
            var range = this.data.kpiClassifications[i+1];
            if(range) {
                range.startClassification = value;
            }
        },
        getMinStartKpiClassification() {
            var minStartKpiClassification = 0;
            if(this.data.kpiClassifications.length > 0)
            minStartKpiClassification = this.data.kpiClassifications[this.data.kpiClassifications.length -1].endClassification;

            return minStartKpiClassification;
        }
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'kpiUpdated',
            type: 'success'
        },
        showWarning: {
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            costFactors: [],
            missingDatasetHeaders: [],
            datasetHeaders: [],
            evaluationList: [],
            data: {
                name: "",
                id: "",
                kpiClassifications: []
            },
            showKeyPerformanceIndicatorDeleteModal: false,
            keyPerformanceIndicatorToDeleteId: 0,
            modelingName: null,
            assetTypeName: null,
            pending: true,
            isUserAdmin: false
        }
    }
};
</script>